/**
 * Workspace-Based Property Management System
 * 
 * Core interfaces for workspace collaboration across all user roles:
 * - Property Managers (personal & professional workspaces)
 * - Tenants (personal & household workspaces)
 * - Contractors (personal & business workspaces)
 * - Real Estate Agents (personal & agency workspaces)
 * 
 * @version 2.0.0
 */

import { Timestamp } from '@angular/fire/firestore';
import { WorkspaceComp } from '../utils/workspace-comp';

/**
 * Workspace Types
 * - personal: For individual users, cannot be shared with property managers
 * - professional: For property management companies (shared collaboration)
 * - household: For tenant families/roommates
 * - business: For contractor companies
 * - agency: For real estate agencies
 * - service_provider: For service providers (plumbers, electricians, etc.)
 * - tenant: For tenants
 * - syndic: For co-ownership building managers (manage buildings + units +
 *   charge statements; project building data into linked owner workspaces
 *   via the projection engine)
 */
export type WorkspaceType = 'personal' | 'professional' | 'household' | 'business' | 'agency' | 'service_provider' | 'tenant' | 'syndic';

/**
 * Workspace Member Roles
 * - owner: Full control, billing responsible, only one per workspace
 * - admin: Full permissions except workspace deletion, can manage members
 * - standard: Read/write/delete data, cannot manage members
 * - viewer: Read-only access
 */
export type WorkspaceRole = 'owner' | 'admin' | 'manager' | 'standard' | 'viewer';

/**
 * Workspace Member
 * Represents a user's membership in a workspace
 */
export interface WorkspaceMember {
  userId: string;
  email: string;
  displayName: string;
  role: WorkspaceRole;
  joinedAt: Date | Timestamp;
  invitedBy: string;
  lastActiveAt?: Date | Timestamp;
}

/**
 * Subscription Limits
 * Flexible structure to support different billing models
 */
export interface SubscriptionLimits {
  maxProperties: number;        // 9999 = unlimited
  maxMembers: number;            // 9999 = unlimited
  maxStorageMB: number;          // Storage limit
  maxActiveJobs?: number;        // For contractors
  maxActiveListings?: number;    // For agents
  aiContractImportsPerMonth?: number;  // 0 for free, 3 for paid workspaces
}

/**
 * Billing Details
 * Professional billing information for invoices
 */
export interface BillingDetails {
  // Company/Professional Information
  companyName?: string;          // Company name for professional billing
  vatNumber?: string;            // VAT/TVA number (EU)
  taxId?: string;                // Tax identification number
  
  // Contact Information
  email: string;                 // Billing email
  phone?: string;                // Billing phone number
  
  // Address Information
  address: {
    line1: string;               // Street address
    line2?: string;              // Apartment, suite, etc.
    city: string;                // City
    state?: string;              // State/Province/Region
    postalCode: string;          // ZIP/Postal code
    country: string;             // ISO 3166-1 alpha-2 country code (e.g., 'FR', 'BE')
  };
  
  // Stripe Integration
  stripeCustomerId?: string;     // Stripe customer ID
  defaultPaymentMethodId?: string; // Default payment method
  
  // Cached payment method info (synced from Stripe via webhooks for instant UI updates)
  hasPaymentMethod?: boolean;    // Quick check if payment method exists
  paymentMethodLast4?: string;   // Last 4 digits of card
  paymentMethodBrand?: string;   // Card brand (visa, mastercard, etc.)
  paymentMethodExpiry?: string;  // Expiry date MM/YYYY

  // Authoritative entitlement flag — single source of truth for whether the
  // workspace can use paid features (AI imports, premium gating, etc.). Webhooks
  // and the reconciler are the only writers. UI / gates read this directly
  // instead of re-deriving from `subscription.tier` or hitting Stripe live.
  // 'paid' iff hasPaymentMethod === true OR subscription.status ∈ {active, trialing}.
  entitlement?: BillingEntitlement;

  // Orthogonal website add-on gate (agency workspaces). 'active' ONLY when the
  // 29€ website price item is on the subscription AND status === 'active' (NOT
  // trialing — no free-trial website). A card on file never unlocks the website.
  websiteEntitlement?: WebsiteEntitlement;

  // When the mirror was last synchronized with Stripe (webhook write or reconciler run).
  lastSyncedAt?: Date | Timestamp;

  // Last background billing-sync failure (property add/delete not yet reflected
  // in Stripe). Surfaced as a discreet warning on the billing page; cleared on
  // the next successful sync. The daily reconciler is the healer of record.
  lastSyncError?: string;
  lastSyncErrorAt?: Date | Timestamp;
}

/**
 * Billing entitlement — whether the workspace is currently allowed to use
 * paid features. Written by webhooks and the reconciler; never derived
 * client-side. Decoupled from `subscription.tier` so attaching a card
 * unlocks paid features without first requiring a billable property.
 */
export type BillingEntitlement = 'free' | 'paid';

/**
 * Website add-on entitlement — orthogonal to {@link BillingEntitlement}. A card
 * on file (or a trial) never unlocks the public website; only an active
 * subscription carrying the website price item does. 'past_due' marks a lapsed
 * but not-yet-cancelled add-on.
 */
export type WebsiteEntitlement = 'inactive' | 'active' | 'past_due';

/**
 * Workspace Subscription
 * Supports multiple billing models for different workspace types
 */
export interface WorkspaceSubscription {
  tier: 'free' | 'pro_starter' | 'pro_business' | 'pro_enterprise';
  status: 'active' | 'trial' | 'suspended' | 'cancelled';
  billingCycle: 'monthly' | 'annual';
  
  // Metrics for flexible billing
  propertyCount: number;         // For per-property billing (Property Managers)
  memberCount: number;           // For per-user billing
  storageUsedMB: number;         // For storage-based billing
  activeJobsCount?: number;      // For contractors
  activeListingsCount?: number;  // For agents
  
  // Limits based on tier
  limits: SubscriptionLimits;
  
  // Billing information (deprecated - use workspace.billing instead)
  billingEmail?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  // Historical id retained for audit/support after a cancellation clears the
  // live `stripeSubscriptionId`. Never used to talk to Stripe.
  lastStripeSubscriptionId?: string;
  nextBillingDate?: Date | Timestamp;
  currentPeriodStart?: Date | Timestamp;
  currentPeriodEnd?: Date | Timestamp;
  trialEndsAt?: Date | Timestamp;
  cancelledAt?: Date | Timestamp;
  // Pending cancellation (Stripe Customer Portal default = cancel at period
  // end). The subscription stays 'active' until `cancelAt`; the billing page
  // shows an "ending soon" banner instead of treating it as already cancelled.
  cancelAtPeriodEnd?: boolean;
  cancelAt?: Date | Timestamp;

  // Per-seat billing snapshot — AGENCY workspaces only (absent on owners; no
  // migration). Display snapshot; billing math reads includedSeats live from
  // the billing config server-side.
  seatModel?: {
    includedSeats: number;
    billableSeats: number;
    extraSeats: number;
    websiteAddon: boolean;
    lastSeatSyncAt?: Date | Timestamp;
  };
}

/**
 * Workspace Statistics
 * Cached counts for performance
 */
export interface WorkspaceStats {
  propertyCount: number;
  contractCount: number;
  tenantCount: number;
  documentCount: number;
  expenseCount?: number;
  repairCount?: number;
  totalValueEUR: number;         // Sum of all property values
  lastActivityAt: Date | Timestamp;
}

/**
 * Workspace
 * Main workspace document containing all metadata and member information
 */
export interface WorkspacePreferences {
  currency: string;              // Base currency for workspace (EUR, USD, GBP, etc.)
  // Future: taxRegion, defaultLanguage, etc.
}

export type OnboardingOwnerType = 'solo' | 'multi' | 'pro' | 'invest';
export type OnboardingPlan = 'explorer' | 'cloud' | 'autopilot';
export type OnboardingPath = 'add_property' | 'simulation' | 'deferred';

export interface WorkspaceOnboarding {
  completed: boolean;
  currentStep: number;                         // 0..7 — source of truth for resume
  startedAt: Date | Timestamp;
  completedAt?: Date | Timestamp;
  skipped?: boolean;
  skippedAt?: Date | Timestamp;
  displayName?: string;                        // step 3
  ownerType?: OnboardingOwnerType;             // step 3
  emailSlug?: string;                          // step 4 (mirrored in workspace_email_aliases)
  selectedPlan?: OnboardingPlan;               // step 6 — intent only
  pathChosen?: OnboardingPath;                 // step 5
  showDashboardTour: boolean;                  // step 7 flips to true
  schemaVersion: 1;
}

export interface Workspace {
  workspaceId: string;
  name: string;
  type: WorkspaceType;
  
  // Ownership
  ownerId: string;
  ownerEmail: string;
  
  // Visual customization
  color?: string;                // Hex color for UI differentiation
  icon?: string;                 // Emoji or icon identifier

  // Professional contact — who a locked-out tenant can reach (#494).
  // Distinct from `ownerEmail` (personal login address) and `billing`
  // (invoicing identity, function-only).
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;

  // Workspace Preferences (added for currency management)
  preferences?: WorkspacePreferences;
  
  // Members
  memberCount: number;           // Cached count
  members: {
    [userId: string]: WorkspaceMember;
  };
  
  // Subscription (only for non-free workspaces)
  subscription?: WorkspaceSubscription;

  // Professional Billing Details
  billing?: BillingDetails;      // Comprehensive billing information

  // Complimentary/beta grant — free + unlimited, no Stripe. Read via
  // isWorkspaceCompActive, never `.enabled` directly. Function-only field.
  comp?: WorkspaceComp;

  // Post-signup onboarding state (owner workspaces)
  onboarding?: WorkspaceOnboarding;

  // Statistics (cached for performance)
  stats: WorkspaceStats;
  
  // Metadata
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
  deletedAt?: Date | Timestamp;  // Soft delete (30-day recovery)
  
  // Schema versioning for future migrations
  schemaVersion: string;         // "2.0.0"
}

/**
 * Workspace Class
 * Provides helper methods and type conversion
 */
export class WorkspaceModel implements Workspace {
  workspaceId: string;
  name: string;
  type: WorkspaceType;
  ownerId: string;
  ownerEmail: string;
  color?: string;
  icon?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  preferences?: WorkspacePreferences;
  memberCount: number;
  members: { [userId: string]: WorkspaceMember };
  subscription?: WorkspaceSubscription;
  billing?: BillingDetails;
  comp?: WorkspaceComp;
  onboarding?: WorkspaceOnboarding;
  stats: WorkspaceStats;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  schemaVersion: string;

  constructor(data: Partial<Workspace> = {}) {
    this.workspaceId = data.workspaceId || '';
    this.name = data.name || '';
    this.type = data.type || 'personal';
    this.ownerId = data.ownerId || '';
    this.ownerEmail = data.ownerEmail || '';
    this.color = data.color;
    this.icon = data.icon;
    this.contactName = data.contactName;
    this.contactEmail = data.contactEmail;
    this.contactPhone = data.contactPhone;
    this.preferences = data.preferences;
    this.memberCount = data.memberCount || 0;
    this.members = data.members || {};
    this.subscription = data.subscription;
    this.billing = data.billing;
    this.comp = data.comp;
    this.onboarding = data.onboarding;
    this.schemaVersion = data.schemaVersion || '2.0.0';
    
    // Initialize stats with defaults
    this.stats = data.stats || {
      propertyCount: 0,
      contractCount: 0,
      tenantCount: 0,
      documentCount: 0,
      totalValueEUR: 0,
      lastActivityAt: new Date()
    };
    
    // Handle Firestore Timestamps
    if (data.createdAt) {
      this.createdAt = data.createdAt instanceof Timestamp 
        ? data.createdAt.toDate() 
        : data.createdAt as Date;
    } else {
      this.createdAt = new Date();
    }
    
    if (data.updatedAt) {
      this.updatedAt = data.updatedAt instanceof Timestamp 
        ? data.updatedAt.toDate() 
        : data.updatedAt as Date;
    } else {
      this.updatedAt = new Date();
    }
    
    if (data.deletedAt) {
      this.deletedAt = data.deletedAt instanceof Timestamp 
        ? data.deletedAt.toDate() 
        : data.deletedAt as Date;
    }
    
    // Convert member timestamps
    if (this.members) {
      Object.keys(this.members).forEach(userId => {
        const member = this.members[userId];
        if (member.joinedAt instanceof Timestamp) {
          member.joinedAt = member.joinedAt.toDate();
        }
        if (member.lastActiveAt instanceof Timestamp) {
          member.lastActiveAt = member.lastActiveAt.toDate();
        }
      });
    }
    
    // Convert subscription timestamps
    if (this.subscription) {
      if (this.subscription.nextBillingDate instanceof Timestamp) {
        this.subscription.nextBillingDate = this.subscription.nextBillingDate.toDate();
      }
      if (this.subscription.currentPeriodStart instanceof Timestamp) {
        this.subscription.currentPeriodStart = this.subscription.currentPeriodStart.toDate();
      }
      if (this.subscription.currentPeriodEnd instanceof Timestamp) {
        this.subscription.currentPeriodEnd = this.subscription.currentPeriodEnd.toDate();
      }
      if (this.subscription.trialEndsAt instanceof Timestamp) {
        this.subscription.trialEndsAt = this.subscription.trialEndsAt.toDate();
      }
      if (this.subscription.cancelledAt instanceof Timestamp) {
        this.subscription.cancelledAt = this.subscription.cancelledAt.toDate();
      }
    }
    
    // Convert stats timestamp
    if (this.stats.lastActivityAt instanceof Timestamp) {
      this.stats.lastActivityAt = this.stats.lastActivityAt.toDate();
    }

    // Convert onboarding timestamps
    if (this.onboarding) {
      if (this.onboarding.startedAt instanceof Timestamp) {
        this.onboarding.startedAt = this.onboarding.startedAt.toDate();
      }
      if (this.onboarding.completedAt instanceof Timestamp) {
        this.onboarding.completedAt = this.onboarding.completedAt.toDate();
      }
      if (this.onboarding.skippedAt instanceof Timestamp) {
        this.onboarding.skippedAt = this.onboarding.skippedAt.toDate();
      }
    }
  }

  /**
   * Check if workspace is deleted (soft delete)
   */
  isDeleted(): boolean {
    return !!this.deletedAt;
  }

  /**
   * Check if workspace is a personal workspace
   */
  isPersonal(): boolean {
    return this.type === 'personal';
  }

  /**
   * Check if workspace is a professional/shared workspace
   */
  isProfessional(): boolean {
    return ['professional', 'household', 'business', 'agency', 'service_provider'].includes(this.type);
  }

  /**
   * Check if workspace is a service provider workspace
   */
  isServiceProvider(): boolean {
    return this.type === 'service_provider';
  }

  /**
   * Check if workspace is a tenant workspace
   */
  isTenant(): boolean {
    return this.type === 'tenant';
  }

  /**
   * Check if workspace has active subscription
   */
  hasActiveSubscription(): boolean {
    return this.subscription?.status === 'active';
  }

  /**
   * Check if workspace is in trial period
   */
  isInTrial(): boolean {
    if (!this.subscription?.trialEndsAt) return false;
    const trialEnd = this.subscription.trialEndsAt instanceof Date 
      ? this.subscription.trialEndsAt 
      : new Date();
    return trialEnd > new Date();
  }

  /**
   * Get user's role in workspace
   */
  getUserRole(userId: string): WorkspaceRole | null {
    return this.members[userId]?.role || null;
  }

  /**
   * Check if user is workspace owner
   */
  isOwner(userId: string): boolean {
    return this.ownerId === userId;
  }

  /**
   * Check if user is admin or owner
   */
  isAdminOrOwner(userId: string): boolean {
    const role = this.getUserRole(userId);
    return role === 'owner' || role === 'admin';
  }

  /**
   * Check if user can edit data
   */
  canUserEdit(userId: string): boolean {
    const role = this.getUserRole(userId);
    return ['owner', 'admin', 'standard'].includes(role || '');
  }

  /**
   * Check if user can delete data
   */
  canUserDelete(userId: string): boolean {
    const role = this.getUserRole(userId);
    return ['owner', 'admin', 'standard'].includes(role || '');
  }

  /**
   * Check if user can manage members
   */
  canManageMembers(userId: string): boolean {
    const role = this.getUserRole(userId);
    return role === 'owner' || role === 'admin';
  }

  /**
   * Check if user is viewer only
   */
  isViewerOnly(userId: string): boolean {
    return this.getUserRole(userId) === 'viewer';
  }

  /**
   * Get workspace display icon based on type
   */
  getWorkspaceIcon(): string {
    if (this.icon) return this.icon;

    switch (this.type) {
      case 'personal':
        return '👤';
      case 'professional':
        return '🏢';
      case 'household':
        return '🏠';
      case 'business':
        return '🔧';
      case 'agency':
        return '🏘️';
      case 'service_provider':
        return '🛠️';
      case 'tenant':
        return '🏡';
      default:
        return '📁';
    }
  }

  /**
   * Get workspace default color based on type
   */
  getWorkspaceColor(): string {
    if (this.color) return this.color;

    switch (this.type) {
      case 'personal':
        return '#6366F1';  // Indigo
      case 'professional':
        return '#3B82F6';  // Blue
      case 'household':
        return '#10B981';  // Green
      case 'business':
        return '#F59E0B';  // Orange
      case 'agency':
        return '#8B5CF6';  // Purple
      case 'service_provider':
        return '#059669';  // Emerald
      case 'tenant':
        return '#0891B2';  // Cyan
      default:
        return '#6B7280';  // Gray
    }
  }

  /**
   * Get member list as array
   */
  getMembersArray(): WorkspaceMember[] {
    return Object.values(this.members);
  }

  /**
   * Get member count excluding owner
   */
  getCollaboratorCount(): number {
    return Object.keys(this.members).filter(userId => userId !== this.ownerId).length;
  }

  /**
   * Check if workspace is at member limit
   */
  isAtMemberLimit(): boolean {
    if (!this.subscription?.limits.maxMembers) return false;
    return this.memberCount >= this.subscription.limits.maxMembers;
  }

  /**
   * Check if workspace is at property limit
   */
  isAtPropertyLimit(): boolean {
    if (!this.subscription?.limits.maxProperties) {
      // Free tier default limit
      return this.stats.propertyCount >= 1;
    }
    return this.stats.propertyCount >= this.subscription.limits.maxProperties;
  }

  /**
   * Convert to plain object for Firestore
   */
  toFirestore(): Record<string, unknown> {
    return {
      workspaceId: this.workspaceId,
      name: this.name,
      type: this.type,
      ownerId: this.ownerId,
      ownerEmail: this.ownerEmail,
      color: this.color,
      icon: this.icon,
      contactName: this.contactName,
      contactEmail: this.contactEmail,
      contactPhone: this.contactPhone,
      memberCount: this.memberCount,
      members: this.members,
      subscription: this.subscription,
      onboarding: this.onboarding,
      stats: this.stats,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      deletedAt: this.deletedAt,
      schemaVersion: this.schemaVersion
    };
  }

  /**
   * Whether the workspace should be routed through the onboarding flow.
   */
  needsOnboarding(): boolean {
    if (!ownerLikeWorkspaceType(this.type)) return false;
    return !this.onboarding?.completed;
  }
}

/**
 * Workspace types eligible for the owner onboarding flow.
 * Service providers and tenants have their own dedicated flows.
 */
export function ownerLikeWorkspaceType(type: WorkspaceType): boolean {
  return type === 'personal' || type === 'professional'
    || type === 'household' || type === 'business';
}

/**
 * Whether a workspace's OWN `properties`/`contracts`/`tenants`/`propertyGroups`
 * collections carry real data (#441 lot 3).
 *
 * False only for `tenant` and `service_provider`: their portals read
 * projections (`contract_projections`, `repair_requests`, `job_requests`) from
 * their own workspace, so the base collections there are permanently empty —
 * reading them is a wasted round trip, not a smaller one. Every other type
 * (owner-like, `agency`, `syndic`) manages real properties and stays on the
 * normal path. `null`/`undefined` (type not resolved yet) stays conservative —
 * treated as needing the core stores, same as before this lot existed.
 *
 * Takes a bare `string`, not `WorkspaceType`: `WorkspaceStore.currentWorkspace()`
 * resolves through `@core/services/auth/workspace.service.types`' `Workspace`
 * interface (`type?: string`) — a second, looser `Workspace` shape that
 * predates this one and is frozen debt, not something to unify here (see
 * CLAUDE.md). The comparison below is a plain string equality either way.
 */
export function coreStoresApplicable(type: string | null | undefined): boolean {
  if (!type) return true;
  return type !== 'tenant' && type !== 'service_provider';
}

export function createInitialOnboarding(): WorkspaceOnboarding {
  return {
    completed: false,
    currentStep: 0,
    startedAt: new Date(),
    showDashboardTour: false,
    schemaVersion: 1
  };
}

export const ONBOARDING_RESERVED_SLUGS: readonly string[] = [
  'admin', 'support', 'help', 'test', 'demo', 'proporio',
  'root', 'noreply', 'info', 'contact', 'billing', 'security',
  'abuse', 'postmaster', 'webmaster', 'mail', 'no-reply'
];

export const ONBOARDING_SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

/**
 * Helper function to get default workspace color by type
 */
export function getDefaultWorkspaceColor(type: WorkspaceType): string {
  switch (type) {
    case 'personal':
      return '#6366F1';  // Indigo
    case 'professional':
      return '#3B82F6';  // Blue
    case 'household':
      return '#10B981';  // Green
    case 'business':
      return '#F59E0B';  // Orange
    case 'agency':
      return '#8B5CF6';  // Purple
    case 'service_provider':
      return '#059669';  // Emerald
    case 'tenant':
      return '#0891B2';  // Cyan
    case 'syndic':
      return '#7C3AED';  // Violet — co-ownership building manager
    default:
      return '#6B7280';  // Gray
  }
}

/**
 * Helper function to get default workspace icon by type
 */
export function getDefaultWorkspaceIcon(type: WorkspaceType): string {
  switch (type) {
    case 'personal':
      return '👤';
    case 'professional':
      return '🏢';
    case 'household':
      return '🏠';
    case 'business':
      return '🔧';
    case 'agency':
      return '🏘️';
    case 'service_provider':
      return '🛠️';
    case 'tenant':
      return '🏡';
    case 'syndic':
      return '🏢';
    default:
      return '📁';
  }
}

/**
 * Helper function to get workspace type display name
 */
export function getWorkspaceTypeDisplayName(type: WorkspaceType): string {
  switch (type) {
    case 'personal':
      return 'Personal';
    case 'professional':
      return 'Professional';
    case 'household':
      return 'Household';
    case 'business':
      return 'Business';
    case 'agency':
      return 'Agency';
    case 'service_provider':
      return 'Service Provider';
    case 'tenant':
      return 'Tenant';
    case 'syndic':
      return 'Syndic';
    default:
      return 'Unknown';
  }
}

/**
 * Helper function to get role display name
 */
export function getRoleDisplayName(role: WorkspaceRole): string {
  switch (role) {
    case 'owner':
      return 'Owner';
    case 'admin':
      return 'Admin';
    case 'manager':
      return 'Manager';
    case 'standard':
      return 'Member';
    case 'viewer':
      return 'Viewer';
    default:
      return 'Unknown';
  }
}

/**
 * Helper function to check if role can perform action
 */
export function roleCanPerformAction(
  role: WorkspaceRole, 
  action: 'view' | 'edit' | 'delete' | 'manage_members'
): boolean {
  switch (action) {
    case 'view':
      return ['owner', 'admin', 'standard', 'viewer'].includes(role);
    case 'edit':
      return ['owner', 'admin', 'standard'].includes(role);
    case 'delete':
      return ['owner', 'admin', 'standard'].includes(role);
    case 'manage_members':
      return ['owner', 'admin'].includes(role);
    default:
      return false;
  }
}

